import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/utils.js';

export interface CodeBlockProps {
  /** The code/text content to display */
  children: string;
  /** Additional class name for the pre element */
  className?: string;
  /** Maximum height before scrolling (default: max-h-64) */
  maxHeight?: string;
  /** Convert escaped newlines (\n) to actual line breaks for display (default: true) */
  renderEscapedNewlines?: boolean;
}

/**
 * Process content to convert escaped newlines to actual newlines for display
 */
function processEscapedNewlines(content: string): string {
  // Replace literal \n (backslash followed by n) with actual newline
  // This handles JSON stringified content where newlines are escaped
  return content.replace(/\\n/g, '\n');
}

/**
 * CodeBlock displays code/text with a copy button.
 */
export function CodeBlock({
  children,
  className,
  maxHeight = 'max-h-64',
  renderEscapedNewlines = true,
}: CodeBlockProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Copy original content (with escaped newlines) for proper JSON
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Process content for display
  const displayContent = renderEscapedNewlines ? processEscapedNewlines(children) : children;

  return (
    <div className="relative group">
      <button
        onClick={handleCopy}
        className={cn(
          'absolute right-2 top-2 p-1.5 rounded-md',
          'bg-[var(--color-background)]/80 backdrop-blur-sm',
          'border border-[var(--color-border)]',
          'text-[var(--color-muted-foreground)]',
          'opacity-0 group-hover:opacity-100',
          'hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]',
          'transition-all duration-150',
          'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50'
        )}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
      <pre
        className={cn(
          'overflow-y-auto overflow-x-hidden rounded-lg p-4 text-xs',
          'border border-[var(--color-border)] bg-[var(--color-background)]',
          'font-mono text-[var(--color-foreground)]',
          'whitespace-pre-wrap break-all',
          maxHeight,
          className
        )}
      >
        <code className="break-all">{displayContent}</code>
      </pre>
    </div>
  );
}

export default CodeBlock;
